import { useState } from 'react';
import Combobox from '@/components/Combobox';
import { ComboboxPopoverContent } from '@/components/Combobox/ComboboxPopoverContent';
import { ComboboxTriggerShell } from '@/components/Combobox/ComboboxTriggerShell';
import { type ComboboxTriggerWidth, resolveComboboxTriggerWidth } from '@/components/Combobox/comboboxTriggerWidth';
import Icon from '@/components/Icon/Icon';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import type { WorkerType } from '@/constants/workerType';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { cn } from '@/lib/cn';
import { useWorkerTypes } from '@/routes/_staff/person/-hooks/useWorkerType';
interface WorkerTypeComboboxProps {
  workerType: WorkerType;
  workerTypeId?: string;
  workerTypeName?: string;
  setWorkerType: (id: string, name: string) => void;
  disabled?: boolean;
  width?: ComboboxTriggerWidth;
}
export const WorkerTypeCombobox = ({
  workerType,
  workerTypeId,
  workerTypeName,
  setWorkerType,
  disabled,
  width
}: WorkerTypeComboboxProps) => {
  const tc = useCommonTranslation();
  const [searchText, setSearchText] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const valueToLabelCache = useValueToLabelCache();
  const {
    workerTypes: options = [],
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    error
  } = useWorkerTypes(workerType, searchText);
  const handleValueChange = (value: string | null) => {
    const selectedItem = options.find(option => option.id === value);
    if (selectedItem == null) {
      return;
    }
    setWorkerType(selectedItem.id, selectedItem.label);
    setSearchText('');
    setSelectorOpen(false);
  };
  if (error) return <p>{error.message}</p>;

  // 高さは ComboboxPopoverContent（Radix の available-height）が持つため、
  // 高さ指定を含む contentClassName ではなく幅だけを受け取る。
  const {
    widthClassName,
    contentStyle
  } = resolveComboboxTriggerWidth(width);
  return <Popover open={selectorOpen} onOpenChange={setSelectorOpen} modal={true}>
      <ComboboxTriggerShell width={width}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn('relative w-full justify-start overflow-hidden text-ellipsis pr-10', !workerTypeName && !isLoading && 'text-muted-foreground')} disabled={isLoading || disabled}>
            <span className="min-w-0 truncate">
              {workerTypeName || (isLoading ? '' : tc('pleaseSelect'))}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
      </ComboboxTriggerShell>
      <ComboboxPopoverContent className={cn('w-full', widthClassName)} style={contentStyle} align="start" side="bottom" sideOffset={5}>
        <Combobox type="single" value={workerTypeId ?? null} options={options} isFetching={isLoading || isFetchingNextPage} onSearchChange={setSearchText} search={searchText} onValueChange={handleValueChange} valueToLabelCache={valueToLabelCache} selectionDisplayStyle="hidden" onScrollToEnd={fetchNextPage} />
      </ComboboxPopoverContent>
    </Popover>;
};